import type {
  AssetDetailsCollection,
  AssetFileInfo,
} from '../rxdb/assetDetails/assetDetails.types';
import type { AssetsByAreaHeirachyCollection } from '../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import { ComponentChecklistDocument } from '../rxdb/componentChecklist/queryBuilder';
import { ComponentTypeCollection } from '../rxdb/componentType/queryBuilder';
import { ConditionRatingCollection } from '../rxdb/conditionRating/queryBuilder';
import {
  DeficiencyActionedList,
  DeficiencyActionedListCollection,
} from '../rxdb/deficiencyActionedList/queryBuilder';
import { DeficiencyComponentRemoval } from '../rxdb/deficiencyComponentRemoval/queryBuilder';
import {
  DeficiencyFiles,
  DeficiencyFilesCollection,
  DeficiencyFilesDocument,
} from '../rxdb/deficiencyFiles/queryBuilder';
import {
  ComponentAreaInfo,
  DeficiencyList,
  DeficiencyListCollection,
} from '../rxdb/deficiencyList/queryBuilder';
import { EnvironmentalConditionCollection } from '../rxdb/environmentalCondition/queryBuilder';
import { InspectionFiles, InspectionFilesCollection } from '../rxdb/inspectionFiles/queryBuilder';
import { InspectionList, InspectionListCollection } from '../rxdb/inspectionList/queryBuilder';
import { NotificationCollection } from '../rxdb/notification/queryBuilder';
import { OperationalDowntimeCollection } from '../rxdb/operationalDowntime/queryBuilder';
import { PmCheckListCollection } from '../rxdb/pmCheckList/queryBuilder';
import { PreviousReportFilesCollection } from '../rxdb/previousReports/queryBuilder';
import { PreviousTaskCommentsCollection } from '../rxdb/previousTaskComments/queryBuilder';
import { PrimaryCauseOfFailureCollection } from '../rxdb/primaryCauseOfFailure/queryBuilder';
import { RemedialActionCollection } from '../rxdb/remedialAction/queryBuilder';
import { RepairFactorCollection } from '../rxdb/repairFactor/queryBuilder';
// import { RiskAssessmentCollection, RiskAssessments } from '../rxdb/riskAssessment/queryBuilder';
import { SavePmChecklistCollection } from '../rxdb/savepmChecklist/queryBuilder';
import { SignAndStampFile } from '../rxdb/signAndStamp/queryBuilder';
import { SignAndStampUserConsentFileCollection } from '../rxdb/signAndStampUserConsent/queryBuilder';
import { SignAndStampUserLevelCollection } from '../rxdb/signAndStampUserLevel/queryBuilder';
import { TaskList, TaskListCollection } from '../rxdb/taskList/queryBuilder';
import { TrafficSafetyScoreCollection } from '../rxdb/trafficSafetyScore/queryBuilder';
import { UrgencyCollection } from '../rxdb/urgency/queryBuilder';
import { ViewInspector, ViewInspectorCollection } from '../rxdb/viewInspector/queryBuilder';
import { WorkPackageFilesCollection } from '../rxdb/workPackageFiles/queryBuilder';
import { WorkPackagesCollection } from '../rxdb/workPackages/queryBuilder';

export interface BaseEntity {
  readonly dateModified: string;
}

export type AimsDatabaseCollections = {
  workPackages: WorkPackagesCollection;
  inspectionList: InspectionListCollection;
  workPackageFiles: WorkPackageFilesCollection;
  assetDetails: AssetDetailsCollection;
  inspectionFiles: InspectionFilesCollection;
  previousReportFiles: PreviousReportFilesCollection;
  deficiencyList: DeficiencyListCollection;
  deficiencyActionedList: DeficiencyActionedListCollection;
  // riskAssessment: RiskAssessmentCollection;
  deficiencyFiles: DeficiencyFilesCollection;
  primaryCauseOfFailure: PrimaryCauseOfFailureCollection;
  remedialAction: RemedialActionCollection;
  conditionRating: ConditionRatingCollection;
  operationalDowntime: OperationalDowntimeCollection;
  trafficSafetyScore: TrafficSafetyScoreCollection;
  urgency: UrgencyCollection;
  componentType: ComponentTypeCollection;
  componentChecklist: ComponentChecklistDocument;
  assetsByAreaHeirachy: AssetsByAreaHeirachyCollection;
  pmCheckList: PmCheckListCollection;
  savePmChecklist: SavePmChecklistCollection;
  repairFactor: RepairFactorCollection;
  environmentalCondition: EnvironmentalConditionCollection;
  viewInspector: ViewInspectorCollection;
  taskList: TaskListCollection;
  notification: NotificationCollection;
  previousTaskComments: PreviousTaskCommentsCollection;
  signAndStampConsentFileDocument: SignAndStampUserConsentFileCollection;
  signAndStampUserLevel: SignAndStampUserLevelCollection;
};

export enum Discipline {
  'Electrical' = 1,
  'Mechanical',
  'Structural',
  'Shaft',
}

export interface AimsFile extends File {
  updatedName: string;
  fileDescription: string;
  showOnReport: boolean;
  fileSortOrder?: number;
  fileType?: string;
}

export interface Deficiency extends DeficiencyList {
  deficiencyComponentRemoval: DeficiencyComponentRemoval;
  deficiencyActionedList: DeficiencyActionedList;
  deficiencyFiles: DeficiencyFilesDocument[];
}

export interface DeficiencyFileEdited extends DeficiencyFiles {
  fileAimsUUID: string;
  isDelete: string;
  fileName: string;
  fileDescription: string;
  showOnReport: boolean;
  fileSortOrder?: number;
  operation?: number;
  isReInspectedFile: boolean;
  fileComponentVersionUUId: string;
}

export enum FileOperation {
  'noChange' = 0,
  'fileDetailsEdited' = 1,
  'fileBlobEdited' = 2,
  'fileDeleted' = 3,
  'fileCopy' = 4,
}

export interface GroupedBasedOnShowOnReport {
  true: DeficiencyFileEdited[];
  false: DeficiencyFileEdited[];
}

export interface InspectionFilesEdited extends InspectionFiles {
  showOnReport: boolean;
  extension: string;
  operation?: number;
  fileSortOrder?: number;
  isReInspectedFile: boolean;
}
export interface InspectionFilesGrouped {
  true: InspectionFilesEdited[];
  false: InspectionFilesEdited[];
}

export interface GroupedBasedOnShowOnReportForUploading {
  true: AimsFile[];
  false: AimsFile[];
}

export enum Site {
  'Vanscoy' = 8,
}

// export interface RiskAssessmentsModified {
//   fkDeficiencyUUID: string;
//   componentRiskResultId: string;
//   pkComponentRiskUUID: string;
//   componentRiskCategoryRankType: number;
//   riskType: number;
//   riskValue: number;
//   riskName: string;
//   name: string;
//   isChanged: boolean;
//   activeDeficiencyInspectionId: number;
// }
// export interface RiskAssessmentPayload {
//   operationalDowntime: RiskAssessmentsModified;
//   rating: RiskAssessmentsModified;
//   trafficScore?: RiskAssessmentsModified;
//   urgency: RiskAssessmentsModified;
//   environmentalCondition: RiskAssessmentsModified;
// }
export interface DeficiencyComponentRemovalPayload {
  deficiencyId: string;
  deficiencyUUID: string;
  inspectionId: string;
  isActive: boolean;
  comment: string | null;
  componentRemovalId: number | null;
  parentComponentId: number | null;
  parentComponentName: string | null;
  componentRemovalIdBySC: number | null;
  commentBySC: string | null;
  siteId: number;
  disciplineId: number;
  dateCreated: string;
  dateModified: string;
  assetId: number;
  isChanged?: boolean;
  componentVersionInspectionId: number | null;
  componentVersionId?: number | null;
  componentVersionUUId: string;
}
export interface DeficiencyListModified extends DeficiencyList {
  name: string;
  dateObserved: string;
  remedialAction: string;
  remedialActionId: number;
  repairFactorId: number;
  assetCategoryComponentId: number;
  failureCauseCategory: number;
  isCompletedDuringInspection: boolean;
  componentAreaInfo: [ComponentAreaInfo];
  commentInspectorField: string;
  attentionFlag: boolean;
  isChanged?: boolean;
  componentType: string;
  commentObservation: string;
  inspectorOperationalDowntime: number;
  inspectorConditionRating: number;
  inspectorUrgency: number;
  inspectorTrafficScore: number;
  componentVersionUUID: string;
  componentVersionId: number | null;
  componentVersionInspectionId: number | null;
}

export interface InspectionFilesGroupedBasedOnExtension {
  'image/jpeg': InspectionFilesGrouped;
  'video/mp4': InspectionFilesGrouped;
  'application/pdf': InspectionFilesGrouped;
  'application/vnd.openxmlformats-officedocument.wordprocessingm': InspectionFilesGrouped;
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': InspectionFilesGrouped;
  'text/plain': InspectionFilesGrouped;
}
export interface ITaskWiseComments {
  [key: number]: ITaskWiseCommentsObject;
}
export interface ITaskWiseCommentsObject {
  comment: string;
  attentionFlag: boolean;
}

export interface ISignAndStampFile extends File {
  updatedName: string;
  category: number;
  isSaveForFuture: boolean;
  isSignature: boolean;
  fileUUID: string;
}
export interface IInspectorData {
  pkInspector?: number;
  fieldInspectorName: string;
  organizationName: string;
  isActive: boolean;
  isChanged: boolean;
  clerkId?: number;
  pkClerk?: number;
  clerkName?: string;
}
export interface IArrayOfAllInspectors {
  [key: string]: IInspectorData[];
}
export interface IServiceProviderList {
  pkServiceProvider: number;
  organizationName: string;
}
export interface IFieldInspectorList {
  abbrv: string | null;
  comment: string | null;
  companyName: string | null;
  dateModified: string | null;
  emailAddress: string | null;
  firstName: string | null;
  fkServiceProvider: number | null;
  fkUser: number | null;
  isActive: boolean | null;
  isLeadInspector: boolean | null;
  lastName: string | null;
  modifiedBy: string | null;
  name: string | null;
  phone: string | null;
  pkInspector?: number | null;
  primaryAddress: string | null;
  title: string | null;
  clerkId?: number | null;
  pkClerk?: number | null;
  clerkName?: string | null;
}

export interface IPmPayload {
  assetCategoryInspectionTypeCheckListId: number;
  pk_uuid?: string;
  isCompleted: boolean;
  comments: string;
  isCompletedChange: boolean;
  isCommentChange: boolean;
  pkAssetInspectionPMChecklist?: number;
  parentId?: number;
  isActive?: boolean;
}

export interface AdditionalFields {
  _attachments?: object;
  _rev?: string;
  _deleted?: boolean;
  dateCreated?: string;
  dateModified?: string;
  inspectorId?: number;
}
export enum FileCategory {
  'Sign' = 11,
  'Stamp' = 12,
}

export interface ISignAndStampData {
  fileUUID?: string;
  updatedName: string;
  category: number;
  isSignature?: boolean;
  fileData?: File;
  isSaveForFuture?: boolean;
  blob?: Blob;
  isInspection?: boolean;
  inspectionId?: number;
  taskId?: number;
  filePath?: string;
}

export interface IComponentInspectionPayload {
  recommendations: string;
  dateCompleted: string;
}

export interface SignStampFile extends File {
  blob?: Blob;
}

export interface InspectionTaskListType extends InspectionList {
  inspectorNameList?: {
    clerkId: number;
    inspectorId: number;
    name: string;
  }[];
  taskList?: TaskList[];
  isAssignedBackTask?: TaskList | boolean;
}

export interface IViewInspectorDocument extends ViewInspector {
  pkClerk?: number;
  isNew?: boolean;
  fieldInspectorId?: number;
  fieldInspectorName: string;
  clerkName?: string;
  clerkId?: number;
}

export interface INewInspector {
  clerkId?: number;
  pkClerk?: number;
  clerkName?: string;
  fieldInspectorId?: number;
  fieldInspectorName?: string;
}

export interface IInspectorListBasedOnOrganization {
  [key: string]: string[];
}

export interface IStyleButton {
  fieldInspectorId?: number;
  fieldInspectorName?: string;
  isActive?: boolean;
  taskUuid?: string;
  count?: number;
  pkInspector?: number;
  firstName?: string;
  lastName?: string;
  clerkId?: number;
  clerkName?: string;
}

export interface ISignAndStampFileBulk extends SignAndStampFile {
  dateCreated?: string;
  dateModified?: string;
  isBulkOperation?: boolean;
  isLiStamp?: boolean;
}

export interface IInspectionTask {
  inspectionId: number;
  taskId: number;
  isLiFiScSame?: boolean;
}

export interface IAssetDetailListFeed {
  assetFiles: AssetFileInfo[];
}

export interface IWorkPackageFileDetailsFeed {
  fileId: string;
  workPackageId: string;
  siteId: number;
  disciplineId: number;
}

export interface IInspectionFilesFeed {
  fileId: string;
  fileUUID: string;
  isDelete: string;
  siteId: number;
  disciplineId: number;
  sizeMb: number;
  extension: string;
}

export interface IDeficiencyFilesFeed {
  fileAimsUUID: string;
  isDelete: string;
  siteId: number;
  disciplineId: number;
  isActiveDeficiencyFile: boolean;
}

export interface IAssetInspectionReportFilesFeed {
  pkAssetInspection: string;
  site: number;
  discipline: number;
}

export interface SizeDimention {
  size: {
    width: number;
    height: number;
  };
}

export interface CurrentUser {
  inspectorId: number;
  name: string;
  userId: number;
  clerkId: number;
}

export interface DeficiencySortByFilter {
  name: string;
  componentType: string;
  conditionRating: string;
  remedialAction: string;
  urgency: string;
  areaId: string;
  dateObserved: string;
  repairFactor: string;
  actionType: string;
  status: string;
  removedFromPlanning: string;
}

export interface DeficiencyView {
  condensed: boolean;
  card: boolean;
}

export interface FileSaveRequestInput {
  filePath: string;
  taskId: number;
  inspectionId: number;
}

export interface deficiencyAction {
  actionType: string;
  actionId: number;
  actionStatus: string;
}

export interface IMigrateRisk {
  fkDeficiencyUUID: string;
  fkRiskType: number;
  riskValue: number;
}
